import { useState } from 'react';

import { Button, Dropdown, FieldLabel, NumericInput, showSnackbar, TextArea } from 'fluxo-ui';
import { ArrowRightIcon, ChevronLeftIcon, SaveIcon } from 'fluxo-ui/icons';

import type { ComponentEvent, ListItem } from 'fluxo-ui';

interface QuestionGrading {
    questionId: string;
    questionNumber: number;
    questionTitle: string;
    questionType: string;
    questionContent: string;
    maxMarks: number;
    correctAnswer?: string;
    studentAnswer: string;
    marksAwarded?: number;
    feedback?: string;
    gradingStatus: 'pending' | 'graded';
}

interface AttemptGrading {
    attemptId: string;
    studentName: string;
    studentEmail: string;
    examTitle: string;
    questions: QuestionGrading[];
}

interface GradingInterfaceProps {
    attempt: AttemptGrading;
    onSave?: (gradingData: { questionId: string; marksAwarded: number; feedback: string }[]) => Promise<void>;
    onNext?: () => void;
    onPrevious?: () => void;
    hasNext?: boolean;
    hasPrevious?: boolean;
}

const GradingInterface: React.FC<GradingInterfaceProps> = ({
    attempt,
    onSave,
    onNext,
    onPrevious,
    hasNext = false,
    hasPrevious = false,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [gradingData, setGradingData] = useState<Map<string, { marks: number; feedback: string }>>(
        new Map(
            attempt.questions.map((q) => [
                q.questionId,
                {
                    marks: q.marksAwarded ?? 0,
                    feedback: q.feedback ?? '',
                },
            ]),
        ),
    );
    const [saving, setSaving] = useState(false);
    const [filterStatus, setFilterStatus] = useState<string>('all');

    const currentQuestion = attempt.questions[currentIndex];
    const currentGrading = gradingData.get(currentQuestion.questionId) || { marks: 0, feedback: '' };

    const filterOptions: ListItem[] = [
        { label: 'All Questions', value: 'all' },
        { label: 'Pending Only', value: 'pending' },
        { label: 'Graded Only', value: 'graded' },
    ];

    const filteredQuestions = attempt.questions.filter((q) => {
        if (filterStatus === 'pending') return q.gradingStatus === 'pending';
        if (filterStatus === 'graded') return q.gradingStatus === 'graded';
        return true;
    });

    const updateGrading = (questionId: string, field: 'marks' | 'feedback', value: number | string) => {
        const updated = new Map(gradingData);
        const current = updated.get(questionId) || { marks: 0, feedback: '' };
        updated.set(questionId, {
            ...current,
            [field]: value,
        });
        setGradingData(updated);
    };

    const handleNext = () => {
        if (currentIndex < filteredQuestions.length - 1) {
            setCurrentIndex((prev) => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex((prev) => prev - 1);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const gradingArray = Array.from(gradingData.entries()).map(([questionId, data]) => ({
                questionId,
                marksAwarded: data.marks,
                feedback: data.feedback,
            }));
            await onSave?.(gradingArray);
            showSnackbar('Grading saved successfully', 'Success', { type: 'success' });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to save grading', 'Error', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleQuickGrade = (percentage: number) => {
        const marks = Math.round((currentQuestion.maxMarks * percentage) / 100);
        updateGrading(currentQuestion.questionId, 'marks', marks);
    };

    const stats = {
        total: attempt.questions.length,
        graded: attempt.questions.filter((q) => q.gradingStatus === 'graded').length,
        pending: attempt.questions.filter((q) => q.gradingStatus === 'pending').length,
    };

    const progress = (stats.graded / stats.total) * 100;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{attempt.examTitle}</h2>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Manual Grading Interface</p>
                    </div>
                    <Button
                        label="Save All Grades"
                        leftIcon={SaveIcon}
                        variant="primary"
                        onClick={handleSave}
                        isLoading={saving}
                        disabled={saving}
                    />
                </div>

                {/* Student Info */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                    <div>
                        <div className="font-medium text-slate-900 dark:text-white">{attempt.studentName}</div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">{attempt.studentEmail}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-sm text-slate-600 dark:text-slate-400">Attempt ID</div>
                        <div className="font-mono text-sm text-slate-900 dark:text-white">{attempt.attemptId}</div>
                    </div>
                </div>

                {/* Progress */}
                <div className="mt-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                        <div className="text-slate-600 dark:text-slate-400">Grading Progress</div>
                        <div className="font-semibold text-slate-900 dark:text-white">
                            {stats.graded} / {stats.total} ({progress.toFixed(0)}%)
                        </div>
                    </div>
                    <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3">
                        <div className="bg-green-500 h-3 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                    </div>
                </div>

                {/* Filter */}
                <div className="mt-4">
                    <Dropdown
                        options={filterOptions}
                        value={filterStatus}
                        onChange={(e: ComponentEvent<string>) => {
                            setFilterStatus(e.value);
                            setCurrentIndex(0);
                        }}
                    />
                </div>
            </div>

            {/* Question Navigation */}
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900 dark:text-white">
                        Question {currentQuestion.questionNumber} of {attempt.questions.length}
                    </h3>
                    <div className="flex items-center gap-2">
                        <Button
                            label="Previous"
                            leftIcon={ChevronLeftIcon}
                            variant="default"
                            layout="outlined"
                            size="sm"
                            onClick={handlePrevious}
                            disabled={currentIndex === 0}
                        />
                        <Button
                            label="Next"
                            rightIcon={ArrowRightIcon}
                            variant="default"
                            layout="outlined"
                            size="sm"
                            onClick={handleNext}
                            disabled={currentIndex === filteredQuestions.length - 1}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-5 gap-2">
                    {filteredQuestions.map((q, index) => (
                        <button
                            key={q.questionId}
                            onClick={() => setCurrentIndex(index)}
                            className={`p-2 rounded text-sm font-medium transition-colors ${
                                index === currentIndex
                                    ? 'bg-teal-500 text-white'
                                    : q.gradingStatus === 'graded'
                                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                                      : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                            }`}
                        >
                            Q{q.questionNumber}
                        </button>
                    ))}
                </div>
            </div>

            {/* Grading Area */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Question & Answer */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                            Question {currentQuestion.questionNumber}
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Question:</div>
                                <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                                    <div className="text-slate-900 dark:text-white">{currentQuestion.questionTitle}</div>
                                    <div className="text-sm text-slate-600 dark:text-slate-400 mt-2">{currentQuestion.questionContent}</div>
                                </div>
                            </div>

                            {currentQuestion.correctAnswer && (
                                <div>
                                    <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                        Expected Answer / Rubric:
                                    </div>
                                    <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                        <div className="text-green-900 dark:text-green-100 text-sm">{currentQuestion.correctAnswer}</div>
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Student's Answer:</div>
                                <div className="p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                                    <div className="text-teal-900 dark:text-teal-100 whitespace-pre-wrap">
                                        {currentQuestion.studentAnswer || <span className="text-slate-400 italic">No answer provided</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Grading Controls */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Grade This Answer</h3>

                        <div className="space-y-4">
                            {/* Quick Grade Buttons */}
                            <div>
                                <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Quick Grade:</div>
                                <div className="grid grid-cols-5 gap-2">
                                    {[0, 25, 50, 75, 100].map((percentage) => (
                                        <button
                                            key={percentage}
                                            onClick={() => handleQuickGrade(percentage)}
                                            className="p-3 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg font-medium text-sm text-slate-900 dark:text-white transition-colors"
                                        >
                                            {percentage}%
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Marks Input */}
                            <FieldLabel label="Marks Awarded" hint={`Out of ${currentQuestion.maxMarks} marks`} required>
                                <NumericInput
                                    value={currentGrading.marks}
                                    onChange={(e: ComponentEvent<number | undefined>) => updateGrading(currentQuestion.questionId, 'marks', e.value ?? 0)}
                                    min={0}
                                    max={currentQuestion.maxMarks}
                                    step={0.5}
                                />
                            </FieldLabel>

                            {/* Score Display */}
                            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-slate-900 dark:text-white">
                                        {currentGrading.marks} / {currentQuestion.maxMarks}
                                    </div>
                                    <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                                        {((currentGrading.marks / currentQuestion.maxMarks) * 100).toFixed(1)}%
                                    </div>
                                </div>
                            </div>

                            {/* Feedback */}
                            <FieldLabel label="Feedback (Optional)" hint="Provide feedback to help the student understand">
                                <TextArea
                                    value={currentGrading.feedback}
                                    onChange={(e: ComponentEvent<string>) => updateGrading(currentQuestion.questionId, 'feedback', e.value)}
                                    placeholder="Write feedback for the student..."
                                    rows={4}
                                />
                            </FieldLabel>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-3 pt-4">
                                <Button
                                    label="Save & Next"
                                    rightIcon={ArrowRightIcon}
                                    variant="primary"
                                    onClick={() => {
                                        handleSave();
                                        if (currentIndex < filteredQuestions.length - 1) {
                                            handleNext();
                                        }
                                    }}
                                    disabled={saving}
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Tips */}
                    <div className="p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                        <h4 className="font-semibold text-teal-900 dark:text-teal-100 mb-2">Grading Tips</h4>
                        <ul className="space-y-1 text-sm text-teal-800 dark:text-teal-200">
                            <li>• Use quick grade buttons for common percentages</li>
                            <li>• Provide constructive feedback to help students learn</li>
                            <li>• Review the rubric/expected answer before grading</li>
                            <li>• Save frequently to avoid losing your work</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            {(hasPrevious || hasNext) && (
                <div className="flex items-center justify-between">
                    {hasPrevious ? (
                        <Button
                            label="Previous Attempt"
                            leftIcon={ChevronLeftIcon}
                            variant="default"
                            layout="outlined"
                            onClick={onPrevious}
                        />
                    ) : (
                        <div></div>
                    )}
                    {hasNext && (
                        <Button label="Next Attempt" rightIcon={ArrowRightIcon} variant="default" layout="outlined" onClick={onNext} />
                    )}
                </div>
            )}
        </div>
    );
};

export default GradingInterface;
